import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { Enable, Resizable } from "re-resizable"
import { PanoramaView, zIndex } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { ButtonInlineIcon } from "@nova/component-btn"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { useTranslation } from "react-i18next"
import { useUniversalControl } from "plugins/shared/useUniversalControl"
import { closeLayer } from "panels/content/side-panel/thunks"
import { selectSelectedView, setSelectedView } from "./PanoramaSlice"
import dynamic from "next/dynamic"
import { Controls } from "./Controls"
import {
    selectActiveSidePanelModal,
    setModalIsFullScreen,
    setModalIsOverlappingMapToolbox,
} from "panels/content/side-panel/slice"
import { Viewer } from "@photo-sphere-viewer/core"
import { Props, ViewerAPI } from "react-photo-sphere-viewer"
import { usePanorama } from "./usePanorama"
import { Compass } from "./Compass"
import { PanoramaImage } from "@nova/protobuf"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { selectPanoramaImageAndUploadByFlightId } from "features/entity-caching/images/panorama/selectors"
import { selectPanoramaFlightById } from "features/entity-caching/flights/selectors"
import { panoramaImageS3Key } from "./module"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { clearSelection } from "panels/content/side-panel/slice.new"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

const ReactPhotoSphereViewer = dynamic(
    () =>
        import("react-photo-sphere-viewer").then((mod) => {
            const Component = forwardRef<ViewerAPI, Props>((props, ref) => (
                <mod.ReactPhotoSphereViewer {...props} ref={ref} />
            ))
            Component.displayName = "ReactPhotoSphereViewer"
            return Component
        }),
    { ssr: false }
)

const ZOOM_SENSITIVITY = 5

const degToRad = (deg: number) => (deg / 180) * Math.PI

export const PanoramaContainerWithBoundary = () => {
    const activeSidePanelModal = useAppSelector(selectActiveSidePanelModal())

    if (
        !activeSidePanelModal ||
        activeSidePanelModal.type !== "layer" ||
        activeSidePanelModal.layerContentType !== "pano"
    )
        return null

    const flightId = activeSidePanelModal.id

    return <PanoramaContainer flightId={flightId} />
}

const PanoramaContainer = ({ flightId }: { flightId: string }) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!

    const { resetMetadata, setLoading, isLoading: layerIsLoading } = useUniversalControl()
    const isLoading = layerIsLoading(flightId)
    const [hasError, setHasError] = useState(false)
    const pdfPreviewOpen = useAppSelector(selectSnapshotOpen())

    const selectedView = useAppSelector(selectSelectedView())
    const [url, setUrl] = useState<string>()
    const [viewer, setViewer] = useState<Viewer | null>(null)
    const compassRef = useRef(null)
    const [bearing, setBearing] = useState<number>(0)
    const [image, setImage] = useState<PanoramaImage>()
    const [sphereCorrection, setSphereCorrection] = useState<{
        pan: number
        tilt: number
    }>()
    const images = useAppSelector(selectPanoramaImageAndUploadByFlightId(flightId))
    const flight = useAppSelector(selectPanoramaFlightById(flightId))

    const {
        size,
        dimensions,
        splitHeight,
        resizableEdges,
        pointIsOffMap,
        zoomToPointOnMap,
        onResize,
        setStartDimensions,
        setStartSplitHeight,
    } = usePanorama({ selectedView: selectedView })

    const showError = useCallback(() => {
        setLoading(flightId, false)
        setHasError(true)
    }, [flightId, setLoading])

    useEffect(() => {
        if (images.length !== 1) {
            console.error("Too many images uploaded to Panorama")
            return
        }

        const upload = images[0] as PanoramaImage
        const stagedPano = stagedFiles[upload.id]

        setImage(upload)

        setSphereCorrection({
            pan: -degToRad(upload?.flightYaw || 0),
            tilt: degToRad(upload?.flightPitch || 0),
        })

        const abortController = new AbortController()
        if (stagedPano) {
            const imageUrl = URL.createObjectURL(stagedPano)
            setUrl(imageUrl)
        } else {
            fetchImage(abortController.signal)
        }

        zoomToPointOnMap(upload)

        return () => {
            // Wipe the state.
            abortController.abort()
            resetMetadata()
            setBearing(0)
            setHasError(false)
            setUrl(undefined)
        }
        // Will complain that usableUploads is missing, it's okay!
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flightId, dispatch, resetMetadata])

    // Blob URL cleanup
    useEffect(() => {
        return () => {
            if (url) URL.revokeObjectURL(url)
        }
    }, [url])

    useEffect(() => {
        if (selectedView === PanoramaView.Mini) {
            dispatch(setModalIsOverlappingMapToolbox(false))
            dispatch(setModalIsFullScreen(false))
        } else if (selectedView === PanoramaView.Split) {
            // TODO: close any open tools
            dispatch(clearSelection())
            dispatch(setModalIsOverlappingMapToolbox(true))
            dispatch(setModalIsFullScreen(false))
        } else {
            // TODO: close any open tools
            dispatch(clearSelection())
            dispatch(setModalIsOverlappingMapToolbox(true))
            dispatch(setModalIsFullScreen(true))
        }
    }, [selectedView, dispatch, image, zoomToPointOnMap])

    // Cleanup when Panorama view is closed (not switched to a different one).
    useEffect(() => {
        return () => {
            if (viewer && viewer.container && viewer.container.parentNode) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accessing a private class variable, needed to cleanup webgl context, otherwise you will lose your map after switching panos 15 times
                ;(viewer.renderer as unknown as any)?.renderer?.dispose()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ;(viewer.renderer as unknown as any)?.renderer?.forceContextLoss()
                viewer.destroy()
            }

            dispatch(setSelectedView(PanoramaView.Mini))
        }
    }, [viewer, dispatch])

    useEffect(() => {
        if (!viewer) return

        const container = viewer.container

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault()

            const currentZoom = viewer.getZoomLevel()
            const normalizedDelta = (e.deltaY * ZOOM_SENSITIVITY) / (currentZoom || 1)
            const newZoom = Math.min(100, Math.max(1, currentZoom - normalizedDelta))
            viewer.zoom(newZoom)
        }

        container.addEventListener("wheel", handleWheel)

        return () => {
            container.removeEventListener("wheel", handleWheel)
        }
    }, [viewer])

    const imageName = images[0]?.id
    const fetchImage = useCallback(
        async (signal: AbortSignal) => {
            try {
                if (!workspaceId) return showError()

                const s3Key = panoramaImageS3Key(
                    workspaceId,
                    fireId,
                    flight.demoId || flightId,
                    imageName,
                    !!flight.demoId
                )

                const response = await fileManagementApi.fetchFile(s3Key, {
                    isDemo: !!flight.demoId,
                    signal,
                })
                if (!response.ok) throw "Fetch error"
                const blob = await response.blob()
                const imageUrl = URL.createObjectURL(blob)
                setUrl(imageUrl)
            } catch {
                if (!signal.aborted) showError()
            }
        },
        [workspaceId, fireId, flightId, showError, imageName, flight.demoId]
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape" && selectedView !== PanoramaView.Mini) {
                e.preventDefault()
                dispatch(setSelectedView(PanoramaView.Mini))
            }

            const position = viewer?.getPosition()
            if (!position) return

            if (e.key === "ArrowLeft") {
                position.yaw -= (2 / 180) * Math.PI
            } else if (e.key === "ArrowRight") {
                position.yaw += (2 / 180) * Math.PI
            } else if (e.key === "ArrowUp") {
                position.pitch += (2 / 180) * Math.PI
            } else if (e.key === "ArrowDown") {
                position.pitch -= (2 / 180) * Math.PI
            }

            viewer?.rotate(position)
        },
        [viewer, selectedView, dispatch]
    )

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => handleKeyDown(e)
        window.addEventListener("keydown", onKeyDown)

        return () => {
            window.removeEventListener("keydown", onKeyDown)
        }
    }, [handleKeyDown])

    if (pdfPreviewOpen) return null

    return (
        <div
            className={classNames("absolute !pointer-events-none w-full h-full", {
                "m-2": selectedView === 0,
            })}
            style={{
                zIndex: zIndex.MapOverlayContainers,
            }}
        >
            <Resizable
                className="pointer-events-auto"
                minWidth={200}
                minHeight={100}
                maxWidth={"100%"}
                maxHeight={"100%"}
                size={size!}
                enable={resizableEdges as Enable | undefined | false}
                onResizeStart={() => {
                    switch (selectedView) {
                        case 0:
                            setStartDimensions(dimensions)
                            break
                        case 1:
                            setStartSplitHeight(splitHeight)
                            break
                    }
                }}
                onResize={(...args) => onResize(args[3])}
                onResizeStop={(...args) => onResize(args[3])}
                lockAspectRatio={false}
            >
                <div
                    className={classNames("bg-gray-800 w-full h-9 flex items-center", {
                        "rounded-t-md": selectedView === 0,
                    })}
                >
                    <span className="text-sm text-white pl-4 pr-8 whitespace-nowrap overflow-hidden text-ellipsis">
                        {flight.name}
                    </span>
                    <ButtonInlineIcon
                        icon={Icons.Close}
                        onClick={() => {
                            dispatch(setModalIsFullScreen(false))
                            dispatch(setModalIsOverlappingMapToolbox(false))
                            dispatch(closeLayer())
                        }}
                        className="absolute top-0 right-0 text-gray-300 h-9 w-9 !p-0 text-2xl hover:text-gray-0 active:text-gray-300 disabled:text-gray-500"
                    />
                </div>
                <div
                    className={classNames("w-full h-full overflow-hidden relative bg-[#202020]", {
                        "rounded-b-md": selectedView === PanoramaView.Mini,
                    })}
                    style={{ height: "calc(100% - 36px)" }}
                >
                    {url && (
                        <>
                            <ReactPhotoSphereViewer
                                containerClass="w-full h-full object-cover"
                                src={url}
                                navbar={false}
                                height={"100%"}
                                width={"100%"}
                                zoomSpeed={1}
                                mousewheel={false}
                                moveSpeed={2}
                                minFov={5}
                                defaultZoomLvl={25}
                                canvasBackground="#313335"
                                sphereCorrection={sphereCorrection}
                                onReady={(viewer) => {
                                    setLoading(flightId, false)
                                    setViewer(viewer as unknown as Viewer)
                                }}
                                onPositionChange={(_, lng) => {
                                    setBearing((lng / Math.PI) * 180)
                                }}
                            />
                            {!isLoading && (
                                <Controls
                                    className="absolute bottom-4 left-4"
                                    items={[
                                        {
                                            Icon: Icons.PanoramaTopLeft,
                                            isLargeIcon: true,
                                            isSelected: selectedView === PanoramaView.Mini,
                                            onClick: () =>
                                                dispatch(setSelectedView(PanoramaView.Mini)),
                                        },
                                        {
                                            Icon: Icons.PanoramaSplit,
                                            isLargeIcon: true,
                                            isSelected: selectedView === PanoramaView.Split,
                                            onClick: () =>
                                                dispatch(setSelectedView(PanoramaView.Split)),
                                        },
                                        {
                                            Icon: Icons.PanoramaFull,
                                            isLargeIcon: true,
                                            isSelected: selectedView === PanoramaView.Full,
                                            onClick: () =>
                                                dispatch(setSelectedView(PanoramaView.Full)),
                                        },
                                    ]}
                                />
                            )}
                            {!isLoading && (
                                <div className="flex flex-row gap-2 absolute bottom-4 right-4">
                                    <Controls
                                        className="text-gray-100"
                                        style={{
                                            visibility: pointIsOffMap(image) ? "visible" : "hidden",
                                            opacity: pointIsOffMap(image) ? 1 : 0,
                                            transition: "0.25s ease-out",
                                        }}
                                        showSeparator={true}
                                        items={[
                                            {
                                                Icon: Icons.ZoomTo,
                                                onClick: () => zoomToPointOnMap(image),
                                            },
                                        ]}
                                    />
                                    <Controls
                                        className="text-gray-100"
                                        showSeparator={true}
                                        items={[
                                            {
                                                Icon: Icons.Minus,
                                                allowClickAndHold: true,
                                                onClick: () => {
                                                    const currentZoom = viewer?.getZoomLevel()
                                                    const normalizedDelta =
                                                        (64 * ZOOM_SENSITIVITY) / (currentZoom || 1)
                                                    viewer?.zoomOut(normalizedDelta)
                                                },
                                            },
                                            {
                                                Icon: Icons.Plus,
                                                allowClickAndHold: true,
                                                onClick: () => {
                                                    const currentZoom = viewer?.getZoomLevel()
                                                    const normalizedDelta =
                                                        (64 * ZOOM_SENSITIVITY) / (currentZoom || 1)
                                                    viewer?.zoomIn(normalizedDelta)
                                                },
                                            },
                                        ]}
                                    />
                                    <Compass
                                        bearing={bearing}
                                        compassRef={compassRef}
                                        viewer={viewer}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {(!url || isLoading) && !hasError && (
                        <div className="w-full h-full bg-[#202020]">
                            <div
                                className="text-white top-1/2 left-1/2 absolute"
                                style={{ transform: "translate(-50%, -50%)" }}
                            >
                                <NovaSpinner width={60} height={60} />
                            </div>
                        </div>
                    )}
                    {hasError && (
                        <div className="w-full h-full bg-[#202020]">
                            <div
                                className="text-[#C04949] top-1/2 left-1/2 absolute"
                                style={{ transform: "translate(-50%, -50%)" }}
                            >
                                {t("Failed to load panorama.")}
                            </div>
                        </div>
                    )}
                </div>
                {selectedView === 1 && (
                    <div className="flex w-full h-2 justify-center pointer-events-none">
                        <div className="flex w-7 h-3.5 bg-[#333333] -translate-y-1/2 justify-center items-center rounded">
                            <svg
                                width="12"
                                height="6"
                                viewBox="0 0 12 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M0.833008 5.08332V4.41666H11.1663V5.08332H0.833008ZM0.833008 1.58332V0.916656H11.1663V1.58332H0.833008Z"
                                    fill="white"
                                />
                            </svg>
                        </div>
                    </div>
                )}
            </Resizable>
        </div>
    )
}
